/**
 * Cannibalization Tab — Redesigned
 *
 * Features:
 *  - UnifiedProductSearch for new product (single-select) and impacted products (multi-select)
 *  - Paste from Excel dialog (SKU lookup)
 *  - Start date (launch date) picker
 *  - Named saves with notes field
 *  - History panel with deletion
 *  - Independence from Manual Override (separate backend model)
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Chip, Alert,
  Slider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
  CircularProgress, Collapse, Divider,
} from '@mui/material';
import {
  CompareArrows, Delete, Calculate, ContentPaste, Save,
  History, ExpandMore, ChevronRight, Close,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useForecastingFilters } from './ForecastingFilterContext';
import UnifiedProductSearch, { ProductOption } from '../Common/UnifiedProductSearch';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ImpactedProduct {
  product_id: string;
  product_name: string;
  sku: string;
  brand?: string;
  category?: string;
  lifecycle_stage?: string;
  cannibalization_pct: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CannibalizationTab: React.FC = () => {
  const { brands, categories } = useForecastingFilters();
  const queryClient = useQueryClient();
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();

  /* New product (single select) */
  const [newProduct, setNewProduct] = useState<ProductOption[]>([]);

  /* Impacted products */
  const [impactedProducts, setImpactedProducts] = useState<ImpactedProduct[]>([]);

  /* Configuration */
  const [defaultPct, setDefaultPct] = useState(30);
  const [launchDate, setLaunchDate] = useState<Date | null>(new Date());
  const [configName, setConfigName] = useState('');
  const [notes, setNotes] = useState('');
  const [calculated, setCalculated] = useState(false);

  /* Paste dialog */
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteErrors, setPasteErrors] = useState<string[]>([]);
  const [pastePending, setPastePending] = useState(false);

  /* History panel */
  const [historyOpen, setHistoryOpen] = useState(false);

  /* Notifications */
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  /* Saving */
  const [isSaving, setIsSaving] = useState(false);

  /* ---- History query ---- */
  const { data: historyRecords, isLoading: historyLoading } = useQuery({
    queryKey: ['cannibalization-history'],
    queryFn: async () => {
      const response = await api.getCannibalizationRecords({ is_active: true, limit: 100 });
      return response.data;
    },
    enabled: historyOpen,
  });

  /* ---- Handlers ---- */

  const handleNewProductChange = useCallback((products: ProductOption[]) => {
    setNewProduct(products);
    setCalculated(false);
  }, []);

  const handleAddImpactedFromSearch = useCallback((products: ProductOption[]) => {
    const newProductId = newProduct[0]?.product_id;

    setImpactedProducts((prev) => {
      const existingIds = new Set(prev.map((p) => p.product_id));
      const added: ImpactedProduct[] = [];

      products.forEach((p) => {
        if (!existingIds.has(p.product_id) && p.product_id !== newProductId) {
          added.push({
            product_id: p.product_id,
            product_name: p.product_name,
            sku: p.sku,
            brand: p.brand,
            category: p.category,
            lifecycle_stage: p.lifecycle_stage,
            cannibalization_pct: defaultPct,
          });
        }
      });

      // Remove products that were deselected
      const selectedIds = new Set(products.map((p) => p.product_id));
      const kept = prev.filter((p) => selectedIds.has(p.product_id));

      return [...kept, ...added];
    });
    setCalculated(false);
  }, [defaultPct, newProduct]);

  const handleRemoveImpacted = (productId: string) => {
    setImpactedProducts((prev) => prev.filter((p) => p.product_id !== productId));
    setCalculated(false);
  };

  const handlePctChange = (productId: string, pct: number) => {
    setImpactedProducts((prev) =>
      prev.map((p) => (p.product_id === productId ? { ...p, cannibalization_pct: pct } : p)),
    );
    setCalculated(false);
  };

  const handleCalculate = () => {
    if (!newProduct[0] || impactedProducts.length === 0) {
      setSnackbar({ open: true, message: 'Select a new product and at least one impacted product', severity: 'error' });
      return;
    }
    setCalculated(true);
  };

  /* Paste from Excel */
  const handlePasteSubmit = async () => {
    if (!pasteText.trim()) return;
    setPastePending(true);
    setPasteErrors([]);

    const lines = pasteText.split(/[\n\r]+/).map((l) => l.trim()).filter(Boolean);
    const skus = lines.flatMap((l) => l.split(/[\t,;]+/).map((s) => s.trim()).filter(Boolean));

    const matched: ImpactedProduct[] = [];
    const notFound: string[] = [];
    const newProductId = newProduct[0]?.product_id;

    for (const sku of skus) {
      try {
        const resp = await api.getProducts({ search: sku, limit: 1 });
        const results = resp.data;
        if (results && results.length > 0) {
          const p = results[0];
          if (p.product_id !== newProductId && !impactedProducts.some((ip) => ip.product_id === p.product_id) && !matched.some((m) => m.product_id === p.product_id)) {
            matched.push({
              product_id: p.product_id,
              product_name: p.product_name,
              sku: p.sku,
              brand: p.brand,
              category: p.category,
              lifecycle_stage: p.lifecycle_stage,
              cannibalization_pct: defaultPct,
            });
          }
        } else {
          notFound.push(sku);
        }
      } catch {
        notFound.push(sku);
      }
    }

    setImpactedProducts((prev) => [...prev, ...matched]);
    setPasteErrors(notFound);
    setPastePending(false);
    setCalculated(false);

    if (matched.length > 0) {
      setSnackbar({ open: true, message: `Added ${matched.length} product(s) from paste`, severity: 'success' });
    }
    if (notFound.length === 0) {
      setPasteOpen(false);
      setPasteText('');
    }
  };

  /* Save configuration */
  const handleSave = async () => {
    if (!newProduct[0] || impactedProducts.length === 0) {
      setSnackbar({ open: true, message: 'Nothing to save — select products first', severity: 'error' });
      return;
    }
    if (!launchDate) {
      setSnackbar({ open: true, message: 'Please set a launch date', severity: 'error' });
      return;
    }

    setIsSaving(true);

    try {
      const np = newProduct[0];
      const launchStr = launchDate.toISOString();

      for (const ip of impactedProducts) {
        await api.createCannibalizationRecord({
          new_product_id: np.product_id,
          new_product_sku: np.sku,
          new_product_name: np.product_name,
          new_product_category: np.category,
          new_product_brand: np.brand,
          cannibalized_product_id: ip.product_id,
          cannibalized_product_sku: ip.sku,
          cannibalized_product_name: ip.product_name,
          cannibalized_category: ip.category,
          cannibalized_brand: ip.brand,
          launch_date: launchStr,
          cannibalization_ratio: ip.cannibalization_pct,
          notes: configName ? `[${configName}] ${notes}`.trim() : notes || undefined,
          is_cross_brand: np.brand !== ip.brand,
        });
      }

      setSnackbar({ open: true, message: `Saved cannibalization configuration "${configName || 'Untitled'}" with ${impactedProducts.length} impacted product(s)`, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cannibalization-history'] });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  /* Delete history record */
  const handleDeleteRecord = async (cannibalizationId: string) => {
    try {
      await api.deleteCannibalizationRecord(cannibalizationId);
      queryClient.invalidateQueries({ queryKey: ['cannibalization-history'] });
      setSnackbar({ open: true, message: 'Cannibalization record deactivated', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Delete failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    }
  };

  /* ---- Derived ---- */

  const selectedNewProduct = newProduct[0] || null;

  /* Build value array for the impacted product search (for controlled state) */
  const impactedSearchValue: ProductOption[] = impactedProducts.map((ip) => ({
    product_id: ip.product_id,
    product_name: ip.product_name,
    sku: ip.sku,
    brand: ip.brand,
    category: ip.category,
  }));

  /* ---- Render ---- */

  return (
    <FullscreenWrap>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
          Cannibalization Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined" size="small"
            startIcon={<History sx={{ fontSize: 16 }} />}
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            History
          </Button>
          <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        When launching a new product, estimate how much it will cannibalize existing products.
        Select the new SKU, add impacted SKUs (via search or Excel paste), set the cannibalization
        percentage for each, and save the configuration.
      </Typography>

      <Grid container spacing={3}>
        {/* Left column: New Product + Configuration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
              New Product (Cannibalizer)
            </Typography>
            <UnifiedProductSearch
              value={newProduct}
              onChange={handleNewProductChange}
              label="Search new product"
              placeholder="Type SKU or name..."
              singleSelect
              brandFilter={brands}
              categoryFilter={categories}
              size="small"
            />
            {selectedNewProduct && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)', fontSize: '12px' }}>
                <Typography variant="body2"><strong>SKU:</strong> {selectedNewProduct.sku}</Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedNewProduct.product_name}</Typography>
                {selectedNewProduct.brand && <Typography variant="body2"><strong>Brand:</strong> {selectedNewProduct.brand}</Typography>}
                {selectedNewProduct.category && <Typography variant="body2"><strong>Category:</strong> {selectedNewProduct.category}</Typography>}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
              Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Configuration Name"
                  placeholder="e.g. Spring 2026 Launch"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Launch Date"
                    value={launchDate}
                    onChange={(d) => setLaunchDate(d)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Default Cannibalization %: {defaultPct}%
                </Typography>
                <Slider
                  value={defaultPct}
                  onChange={(_, v) => setDefaultPct(v as number)}
                  min={5} max={80} step={5}
                  marks={[
                    { value: 10, label: '10%' },
                    { value: 30, label: '30%' },
                    { value: 50, label: '50%' },
                    { value: 80, label: '80%' },
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ mx: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Notes" multiline rows={2}
                  placeholder="Additional context..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right column: Impacted Products */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Impacted Products
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined" size="small"
                  startIcon={<ContentPaste sx={{ fontSize: 14 }} />}
                  onClick={() => setPasteOpen(true)}
                >
                  Paste from Excel
                </Button>
                <Button
                  variant="contained" size="small"
                  startIcon={<Calculate sx={{ fontSize: 14 }} />}
                  onClick={handleCalculate}
                  disabled={!selectedNewProduct || impactedProducts.length === 0}
                >
                  Calculate
                </Button>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <UnifiedProductSearch
                value={impactedSearchValue}
                onChange={handleAddImpactedFromSearch}
                label="Search impacted products"
                placeholder="Type SKU or name to add..."
                brandFilter={brands}
                categoryFilter={categories}
                size="small"
              />
            </Box>

            {impactedProducts.length > 0 ? (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Item Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px', width: 90 }}>%</TableCell>
                      {calculated && <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11px' }}>Impact</TableCell>}
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px', width: 40 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {impactedProducts.map((ip) => (
                      <TableRow key={ip.product_id} hover>
                        <TableCell sx={{ fontSize: '12px', fontFamily: 'monospace' }}>{ip.sku}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          <Box>
                            {ip.product_name}
                            {ip.brand && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '10px' }}>
                                {ip.brand}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: '6px', px: '8px' }}>
                          {ip.lifecycle_stage ? (
                            <Chip
                              label={ip.lifecycle_stage}
                              size="small"
                              color={
                                ip.lifecycle_stage === 'NEW' ? 'info' :
                                ip.lifecycle_stage === 'GROWTH' ? 'success' :
                                ip.lifecycle_stage === 'DECLINING' ? 'warning' :
                                (ip.lifecycle_stage === 'PHASE_OUT' || ip.lifecycle_stage === 'DISCONTINUED') ? 'error' :
                                'default'
                              }
                              variant="outlined"
                              sx={{ fontSize: '10px', height: 20 }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number" size="small"
                            value={ip.cannibalization_pct}
                            onChange={(e) => handlePctChange(ip.product_id, parseInt(e.target.value) || 0)}
                            inputProps={{ min: 1, max: 100, style: { textAlign: 'center', width: 50, fontSize: '12px', padding: '4px' } }}
                          />
                        </TableCell>
                        {calculated && (
                          <TableCell align="right">
                            <Chip
                              label={`-${ip.cannibalization_pct}%`}
                              color="error" size="small" variant="outlined"
                              sx={{ fontSize: '11px', height: 22 }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleRemoveImpacted(ip.product_id)}>
                            <Close sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 3, fontSize: '13px' }}>
                No impacted products yet. Search above or paste from Excel.
              </Typography>
            )}

            {calculated && impactedProducts.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Impact Summary:</strong> {impactedProducts.length} product(s) will be impacted.
                Average reduction: {Math.round(impactedProducts.reduce((s, p) => s + p.cannibalization_pct, 0) / impactedProducts.length)}%
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Save bar */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="contained" color="success"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={!selectedNewProduct || impactedProducts.length === 0 || isSaving}
            >
              {isSaving ? 'Saving...' : `Save Configuration (${impactedProducts.length} products)`}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* History section */}
      <Box sx={{ mt: 4 }}>
        <Box
          onClick={() => setHistoryOpen(!historyOpen)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: historyOpen ? 2 : 0, userSelect: 'none' }}
        >
          <IconButton size="small" sx={{ p: 0 }}>
            {historyOpen ? <ExpandMore sx={{ fontSize: 20 }} /> : <ChevronRight sx={{ fontSize: 20 }} />}
          </IconButton>
          <Typography sx={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Cannibalization History
          </Typography>
        </Box>
        <Collapse in={historyOpen}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
          ) : historyRecords && historyRecords.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>New Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Impacted Product</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px' }}>Ratio</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Launch Date</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '11px', width: 50 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRecords.map((rec: any) => {
                    /* Extract config name from notes if present */
                    const nameMatch = rec.notes?.match(/^\[(.+?)\]/);
                    const displayName = nameMatch ? nameMatch[1] : '—';
                    return (
                      <TableRow key={rec.cannibalization_id} hover>
                        <TableCell sx={{ fontSize: '12px' }}>{displayName}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.new_product_sku || rec.new_product_name || rec.new_product_id || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.cannibalized_product_sku || rec.cannibalized_product_name || rec.cannibalized_product_id || '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${rec.cannibalization_ratio}%`}
                            size="small" color="warning" variant="outlined"
                            sx={{ fontSize: '11px', height: 22 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {rec.launch_date ? new Date(rec.launch_date).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small" color="error"
                            onClick={() => handleDeleteRecord(rec.cannibalization_id)}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 3, fontSize: '13px' }}>
              No cannibalization records found.
            </Typography>
          )}
        </Collapse>
      </Box>

      {/* Paste from Excel dialog */}
      <Dialog open={pasteOpen} onClose={() => setPasteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Paste SKUs from Excel</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste product SKUs from Excel (one per line, or separated by tabs/commas).
            Each SKU will be looked up and added to the impacted products list.
          </Typography>
          <TextField
            multiline rows={8} fullWidth
            placeholder={"SKU001\nSKU002\nSKU003\n..."}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            disabled={pastePending}
          />
          {pasteErrors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>{pasteErrors.length} SKU(s) not found:</strong>
              <Box sx={{ ml: 1, mt: 0.5, fontSize: '12px' }}>
                {pasteErrors.join(', ')}
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPasteOpen(false); setPasteText(''); setPasteErrors([]); }}>Cancel</Button>
          <Button
            onClick={handlePasteSubmit} variant="contained"
            disabled={!pasteText.trim() || pastePending}
            startIcon={pastePending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {pastePending ? 'Looking up...' : 'Import SKUs'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </FullscreenWrap>
  );
};

export default CannibalizationTab;
